import { Header } from "@/components/Header";
import { StaticRouteBreadcrumbs } from "@/components/StaticRouteBreadcrumbs";
import { SHIPPING_META_DESCRIPTION } from "../../scripts/site-policy.mjs";
import { Footer } from "@/components/Footer";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Helmet } from "react-helmet-async";
import { Truck, Package, Clock, MapPin } from "lucide-react";
import {
  FREE_SHIPPING_THRESHOLD,
  FREE_SHIPPING_COPY,
  FLAT_SHIPPING_COPY,
  DELIVERY_WINDOW_COPY,
  SHIPPING_REGION_COPY,
  RETURN_SHIPPING_COPY,
} from "@/lib/shipping";

// Title and description must match the /shipping entry in scripts/routes.mjs: both heads stay in the document.
const metaDescription = SHIPPING_META_DESCRIPTION;
const pageTitle = `Shipping & Delivery — Free U.S. Shipping Over $${FREE_SHIPPING_THRESHOLD} | LoftLoom`;

const ShippingInfo = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href="https://loftloom.com/shipping" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content="https://loftloom.com/shipping" />
        <meta property="og:type" content="website" />
      </Helmet>
      <AnnouncementBar />
      <Header />
      <StaticRouteBreadcrumbs />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-16 md:py-24">
          <h1 className="page-title font-semibold mb-4 text-center">Shipping</h1>
          <p className="text-muted-foreground text-center max-w-xl mx-auto mb-12">{SHIPPING_REGION_COPY}</p>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {[
              { icon: Truck, title: "Free Shipping", desc: `${FREE_SHIPPING_COPY}. ${FLAT_SHIPPING_COPY}.` },
              { icon: Clock, title: "Delivery Timeline", desc: `${DELIVERY_WINDOW_COPY}.` },
              { icon: MapPin, title: "Where we ship", desc: SHIPPING_REGION_COPY },
              { icon: Package, title: "Returns", desc: `30 days from delivery. ${RETURN_SHIPPING_COPY}` },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4 p-5 border border-border">
                <Icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-8 text-sm text-muted-foreground leading-relaxed">
            <section>
              <h2 className="font-display text-xl font-semibold text-foreground mb-3">How it works</h2>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Place your order — the order confirmation is emailed to you by FurniFindUSA, our fulfilment partner.</li>
                <li>Your order is processed and handed to the carrier.</li>
                <li>Delivery takes {DELIVERY_WINDOW_COPY}.</li>
                <li>Your order arrives by standard carrier delivery. Assembly, where needed, is yours to do.</li>
              </ol>
            </section>
            <section>
              <h2 className="font-display text-xl font-semibold text-foreground mb-3">Tracking your order</h2>
              <p>Once your order ships, you'll receive a tracking link via email. You can also ask about your order status anytime by emailing hello@loftloom.com.</p>
            </section>
            <section>
              <h2 className="font-display text-xl font-semibold text-foreground mb-3">Return shipping</h2>
              <p>{RETURN_SHIPPING_COPY}</p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ShippingInfo;
